import { CloudOff, Columns3 } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SheetColumnDef } from "./sheetColumns.js";

/**
 * The furniture around a sheet: column picker, offline banner, save/sync
 * status (#911, epic #872).
 *
 * These are the parts a user reads as "this is the same kind of screen". They
 * were private to ListSheet, so the Food grid grew its own differently-worded,
 * differently-placed equivalents. Sharing the components — not just the CSS —
 * is what stops the two drifting again the next time either is edited.
 */

export function SheetColumnsMenu<Key extends string>({
  columns,
  visibleKeys,
  onToggle,
  label
}: {
  columns: SheetColumnDef<Key>[];
  visibleKeys: Key[];
  onToggle: (key: Key) => void;
  /** Overrides the menu's own name; defaults to the shared "Columns". */
  label?: string;
}) {
  const { t } = useTranslation("common");
  const ref = useRef<HTMLDetailsElement | null>(null);

  /**
   * Dismiss on a click outside, and on Escape.
   *
   * A bare `<details>` only closes when its own summary is clicked again, so the
   * menu sat open over the sheet while the user carried on working — including
   * over the rows they had opened it to reveal. Native disclosure behaviour is
   * right for a disclosure; this is a menu, and menus close when you leave.
   *
   * `pointerdown` rather than `click`: it fires before focus moves, so the menu
   * is already gone by the time the thing the user actually aimed at reacts.
   */
  useEffect(() => {
    const close = (event: Event) => {
      const details = ref.current;
      if (!details?.open) return;
      if (event.target instanceof Node && details.contains(event.target)) return;
      details.open = false;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const details = ref.current;
      if (!details?.open) return;
      details.open = false;
      // Focus goes back to the control that opened it, or it would be stranded
      // on a node that is no longer visible.
      details.querySelector("summary")?.focus();
    };
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", close, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <details className="list-sheet-columns" ref={ref}>
      <summary><Columns3 size={15} aria-hidden="true" /> {label ?? t("sheet.columns")}</summary>
      <div className="list-sheet-columns-menu">
        {columns.map((column) => (
          <label key={column.key}>
            <input
              type="checkbox"
              checked={visibleKeys.includes(column.key)}
              // A locked column renders disabled rather than hidden, so the
              // user can see it exists and is not left hunting for it.
              disabled={column.always}
              onChange={() => onToggle(column.key)}
            />
            {column.labelKey ? t(column.labelKey) : column.label}
          </label>
        ))}
      </div>
    </details>
  );
}

export function SheetOfflineBanner({ message }: { message?: string }) {
  const { t } = useTranslation("common");
  return (
    <div className="list-sheet-offline" role="status" aria-live="polite">
      <CloudOff size={15} aria-hidden="true" /> {message ?? t("sheet.offline")}
    </div>
  );
}

/**
 * One vocabulary for "is my work safe?", so the two sheets cannot describe the
 * same condition differently.
 *
 * Four states, in this order of precedence: offline (nothing is reaching the
 * server), work actually moving, work sitting still, quiet. The features differ
 * only in HOW the work reaches the server — Shopping syncs in the background,
 * Food commits explicitly — and that difference is carried by `pendingLabel`,
 * not by a second status vocabulary.
 *
 * The wording is passed IN rather than translated here: this stays a pure
 * function so it can be unit-tested without a React tree, and a caller inside a
 * component hands it the strings its own `t` resolved. The English defaults are
 * a floor for a caller that has no `t` at all — a COMPONENT should call
 * `useSheetSyncStatus` below instead of relying on them.
 */
export function sheetSyncStatusLabel(input: {
  offline: boolean;
  pendingCount: number;
  /**
   * Is a save actually in flight or scheduled? (#1122)
   *
   * Required, because the answer used to be assumed. Any unsaved row read as
   * "Saving…", which is a different claim: that the work is on its way. The
   * Food grid showed it beside its own "Can't reach the server" error, and over
   * a draft the caret is still in — which that grid deliberately holds back
   * until focus leaves, so nothing was going to save it. Unsaved and idle is a
   * real state and it now has its own words.
   */
  busy: boolean;
  pendingLabel?: (count: number) => string;
  idleLabel?: string;
  offlineLabel?: string;
  offlineUnsavedLabel?: (count: number) => string;
  unsavedLabel?: (count: number) => string;
}): string {
  const { busy, idleLabel = "All changes saved", offline, offlineLabel = "Offline", pendingCount } = input;
  const pendingLabel = input.pendingLabel ?? (() => "Saving…");
  const offlineUnsavedLabel = input.offlineUnsavedLabel ?? ((count: number) => `Offline - ${count} unsaved`);
  const unsavedLabel = input.unsavedLabel ?? ((count: number) => `${count} unsaved`);
  if (offline) return pendingCount > 0 ? offlineUnsavedLabel(pendingCount) : offlineLabel;
  if (pendingCount > 0) return busy ? pendingLabel(pendingCount) : unsavedLabel(pendingCount);
  return idleLabel;
}

/**
 * The same three states, already in the reader's language.
 *
 * The English defaults above are a floor for a non-React caller, not a
 * vocabulary — and a component that took them got an English footer under a
 * Spanish sheet, which is exactly what the Food grid was doing (#1088). So the
 * words live HERE, resolved once from the `sheet.status.*` copy both sheets
 * already share, and a sheet can no longer ship the wrong language by omitting
 * an argument it did not know it had to pass.
 *
 * `pendingLabel` stays the caller's: the in-flight wording is the one thing the
 * two sheets legitimately differ on, because they differ in how work reaches
 * the server.
 */
export function useSheetSyncStatus(input: {
  offline: boolean;
  pendingCount: number;
  busy: boolean;
  pendingLabel?: (count: number) => string;
}): string {
  const { t } = useTranslation("common");
  return sheetSyncStatusLabel({
    offline: input.offline,
    pendingCount: input.pendingCount,
    busy: input.busy,
    pendingLabel: input.pendingLabel ?? (() => t("sheet.status.saving")),
    idleLabel: t("sheet.status.saved"),
    offlineLabel: t("sheet.status.offline"),
    offlineUnsavedLabel: (count) => t("sheet.status.offlineUnsaved", { count }),
    unsavedLabel: (count) => t("sheet.status.unsaved", { count })
  });
}

export function SheetFooter({
  offline,
  status,
  children
}: {
  offline: boolean;
  status: string;
  /** Feature-specific trailing content: a list total for Shopping, save
   * controls for Food. */
  children?: ReactNode;
}) {
  return (
    <footer className="list-sheet-footer">
      <span className={offline ? "list-sheet-status offline" : "list-sheet-status"} aria-live="polite">{status}</span>
      {children}
    </footer>
  );
}
